import { ArrowLeft, Download, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type { Order } from "../../../types/api";
import { formatCurrency, formatEventDate } from "../../../utils/format";

export default function AdminPrintOrderPage() {
  const { reference } = useParams<{ reference: string }>();
  const { token } = useAdminAuth();
  const { showToast } = useToast();
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!token || !reference) return;
    setIsDownloading(true);
    try {
      const blob = await adminApi.downloadOrderTicketsPdf(token, reference);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tickets-${reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast("Tickets downloaded", "success");
    } catch (downloadError) {
      showToast(
        downloadError instanceof Error ? downloadError.message : "Could not download tickets",
        "error"
      );
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (!token || !reference) return;
    let active = true;

    adminApi
      .getOrder(token, reference)
      .then((response) => {
        if (active) setOrder(response.order);
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(requestError instanceof Error ? requestError.message : "Could not load order");
        }
      });

    return () => {
      active = false;
    };
  }, [reference, token]);

  if (!order && !error) return <LoadingState label="Loading tickets..." />;

  if (error || !order) {
    return (
      <div className="ego-shell flex min-h-screen items-center justify-center px-4">
        <div className="ego-surface px-6 py-12 text-center text-sm font-semibold text-red-600">
          {error ?? "Order not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="ego-shell min-h-screen">
      <style>{`
        @media print {
          html, body { background: #fff !important; }
          .print-toolbar { display: none !important; }
          .print-page { padding: 0 !important; }
          .print-ticket { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; border: 1px dashed #c8cad0 !important; }
          .print-ticket-banner { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="print-toolbar sticky top-0 z-10 border-b border-line bg-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 px-4 py-3 sm:px-6">
          <Link
            to="/admin/screenings"
            className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={14} /> Back to admin
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="ego-btn-secondary"
            >
              <Download size={15} /> {isDownloading ? "Preparing..." : "Download PDF"}
            </button>
            <button type="button" onClick={() => window.print()} className="ego-btn-primary">
              <Printer size={15} /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="print-page mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div className="mb-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Order {order.reference}
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground">{order.event.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatEventDate(order.event.date, order.event.time)} · {order.event.venue}, {order.event.city}
          </p>
        </div>

        <div className="space-y-4">
          {order.tickets.map((ticket, index) => (
            <article
              key={ticket.code}
              className="print-ticket overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_8px_24px_-16px_rgba(11,18,32,0.16)]"
            >
              {order.event.image && (
                <div className="print-ticket-banner relative h-28 w-full overflow-hidden bg-[#0b1220]">
                  <img
                    src={order.event.image}
                    alt={order.event.title}
                    crossOrigin="anonymous"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-black/10" />
                  <div className="absolute inset-0 flex items-end p-4 text-white">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">
                        {order.event.city}
                      </div>
                      <div className="text-base font-bold leading-tight sm:text-lg">
                        {order.event.title}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-[1fr_auto] gap-5 p-5">
                <div className="min-w-0 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="ego-badge ego-badge-brand">{ticket.ticketName}</span>
                    <span className="ego-badge ego-badge-neutral">
                      {String(index + 1).padStart(2, "0")}/
                      {String(order.tickets.length).padStart(2, "0")}
                    </span>
                  </div>

                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Attendee
                    </div>
                    <div className="text-base font-bold text-foreground">{ticket.attendeeName}</div>
                  </div>

                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <dt className="font-semibold uppercase tracking-wide text-muted-foreground">Date</dt>
                      <dd className="text-foreground">{order.event.date}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wide text-muted-foreground">Time</dt>
                      <dd className="text-foreground">{order.event.time}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wide text-muted-foreground">Venue</dt>
                      <dd className="text-foreground">{order.event.venue}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wide text-muted-foreground">City</dt>
                      <dd className="text-foreground">{order.event.city}</dd>
                    </div>
                  </dl>

                  <div className="border-t border-dashed border-line pt-3">
                    <div className="font-mono text-xs tracking-[0.2em] text-foreground">
                      {ticket.code}
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      Present this ticket at the gate. Payment: {order.paymentMethod} ·{" "}
                      {formatCurrency(order.total / Math.max(order.tickets.length, 1))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center rounded-xl border border-line bg-surface p-3">
                  <QRCodeSVG value={ticket.payload} size={140} level="M" />
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Scan at gate
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-6 border-t border-dashed border-line pt-4 text-center text-[11px] text-muted-foreground">
          Issued {new Date(order.createdAt).toLocaleString()} · {order.tickets.length} ticket
          {order.tickets.length === 1 ? "" : "s"} · Total {formatCurrency(order.total)}
        </div>
      </div>
    </div>
  );
}
